import React, { useState } from "react";
import { X } from "lucide-react";
import Button from "components/Button";
import companyLeaveSettingsManager from "services/LeavePolicy/companyLeaveSettingsManager";

/* ────────────────────────────────────────────────────────────────────────
   YearStartMonthDialog — set the single month the company's leave year starts.

   Shows the current selection as a removable chip (× to clear) and a grid of
   months below to pick from. Only one month can be set, so selecting a month
   replaces the current one. Saves via PUT /api/company-leave-settings.
   ──────────────────────────────────────────────────────────────────────── */

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

interface YearStartMonthDialogProps {
  // Current saved month (1–12), or null when none is set yet.
  initialMonth: number | null;
  onSaved: (month: number, message: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
}

const YearStartMonthDialog: React.FC<YearStartMonthDialogProps> = ({
  initialMonth,
  onSaved,
  onError,
  onClose,
}) => {
  const [selected, setSelected] = useState<number | null>(initialMonth);
  const [saving, setSaving] = useState(false);

  const selectedLabel = MONTHS.find((m) => m.value === selected)?.label;
  const canSave = selected != null && selected !== initialMonth && !saving;

  const handleSave = async () => {
    if (selected == null || saving) return;
    setSaving(true);
    try {
      await companyLeaveSettingsManager.update(selected);
      onSaved(selected, "Leave year start month updated successfully");
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      onError(err?.response?.data?.message || "Failed to update leave year start month");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white px-1 dark:bg-gray-800">
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        Choose the month your company&apos;s leave year begins. Only one month can be set.
      </p>

      {/* Selected month — removable chip (single value) */}
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        Selected month
      </p>
      <div className="mb-5 min-h-[36px]">
        {selected != null ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 py-1.5 pl-3 pr-1.5 text-sm font-semibold text-blue-700 dark:border-blue-900/50 dark:bg-blue-900/30 dark:text-blue-300">
            {selectedLabel}
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label="Clear selected month"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-blue-500 transition-colors hover:bg-blue-200/60 hover:text-blue-700 dark:text-blue-300 dark:hover:bg-blue-800/60"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ) : (
          <span className="inline-flex items-center rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-sm text-slate-400 dark:border-slate-600 dark:text-slate-500">
            No month selected
          </span>
        )}
      </div>

      {/* Month list */}
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        Select a month
      </p>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {MONTHS.map((m) => {
          const active = selected === m.value;
          return (
            <button
              key={m.value}
              type="button"
              aria-pressed={active}
              onClick={() => setSelected(m.value)}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 ${active
                ? "border-blue-600 !bg-blue-600 text-white"
                : "border-slate-200 !bg-white text-slate-700 hover:border-blue-400 hover:!bg-blue-50 dark:border-slate-600 dark:!bg-gray-700 dark:text-slate-200 dark:hover:!bg-slate-600"}`}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4 dark:border-slate-700">
        <Button
          onClick={onClose}
          type="button"
          className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700 dark:!bg-slate-700 dark:hover:!bg-slate-600 dark:!text-slate-100"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          type="button"
          loading={saving}
          disabled={!canSave}
          className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default YearStartMonthDialog;
